import { defineStore } from 'pinia'

import {
    detachNumbersToPool,
    getDetailPool,
    updatePool,
    getAvailableNumbersForPool,
    buyAndAttachNumbersToPool,
    autoBuyPool,
} from '@/api/pool-routes'
import {
    getFilterOnPageInLocalStorageByWorkspaceId,
    setFilterOnPageInLocalStorageByWorkspaceId,
} from '@/helpers/save-sorting-local-storage-helper'

import { useDefaultStore } from '@/store/defaultStore'
import { useBreadCrumbsStore } from '@/store/breadCrumbsStore'
import { useDetailNumberPoolTrafficSourcesStore } from '@/store/call-tracking/numberPools/detailNumberPoolTrafficSourcesStore'
import { usePermissionsStore } from '@/store/permissionsStore'
import { useLocalFilterTableStore } from '@/store/localFilterTableStore'

import { FILTER_STATUS } from '@/constants/localFilters'
import { SETTINGS_HEADER_TABLE } from '@/constants/headersTable/settingsHeaderTable'
import { NUMBER_IN_POOLS_HEADERS_TABLE, NUMBER_IN_POOLS_HEADERS_TABLE_WITH_ACTIONS } from '@/constants/headersTable/numberInPoolsHeadersTable'
import { VIEWS_OPTIONS } from '@/constants/call-tracking/numberPool/numberPoolSettingsOptions'

export const useDetailNumberPoolStore = defineStore('detailNumberPool', {
    state: () => ({
        slug: null,
        id: null,
        loading: false,
        updateLoading: false,
        actionLoading: false,
        availableLoading: false,

        formData: {},
        search: '',
        onPage: 25,
        onPageAvailable: 25,
        attachedNumbers: [],
        purchasedNumbers: [],

        attachedNumbersFilters: [
            FILTER_STATUS,
        ],

        availableNumbers: [],
        availableNumbersFilters: [
            FILTER_STATUS,
        ],

        currentView: VIEWS_OPTIONS.setupNumberPool,
        selected: [],
    }),

    getters: {
        headersByPermission() {
            const permissionsStore = usePermissionsStore()

            return permissionsStore.permissions.numberPools.edit
                ? NUMBER_IN_POOLS_HEADERS_TABLE_WITH_ACTIONS.map((head, index) => ({
                    ...head,
                    orderIndex: index,
                }))
                : NUMBER_IN_POOLS_HEADERS_TABLE.map((head, index) => ({
                    ...head,
                    orderIndex: index,
                }))
        },
    },

    actions: {
        getQueryLocalStorage() {
            const detailNumberPoolTrafficSourcesStore = useDetailNumberPoolTrafficSourcesStore()
            const localFilterTableStore = useLocalFilterTableStore()
            localFilterTableStore.initializeTableSettings(SETTINGS_HEADER_TABLE.numberInPool, this.attachedNumbersFilters)
            localFilterTableStore.setHeaders(SETTINGS_HEADER_TABLE.numberInPool, this.headersByPermission)
            localFilterTableStore.getQueryLocalStorage(SETTINGS_HEADER_TABLE.numberInPool, this.id)
            this.getLocalStorageOnPage()
            detailNumberPoolTrafficSourcesStore.getQueryLocalStorage()
        },

        getQueryLocalStorageForAvailableNumbers() {
            const detailNumberPoolTrafficSourcesStore = useDetailNumberPoolTrafficSourcesStore()
            const localFilterTableStore = useLocalFilterTableStore()
            localFilterTableStore.initializeTableSettings(SETTINGS_HEADER_TABLE.availableNumbersInNumberPool, this.availableNumbersFilters)
            localFilterTableStore.getQueryLocalStorage(SETTINGS_HEADER_TABLE.availableNumbersInNumberPool, this.id)
            this.getLocalStorageAvailableOnPage()
            detailNumberPoolTrafficSourcesStore.getQueryLocalStorage()
        },

        setNumberPoolSlug(slug) {
            this.slug = slug
        },

        setNumberPoolId(id) {
            this.id = id
        },

        setCurrentView(item) {
            if (item.hasOwnProperty('view')) {
                this.currentView = item.view

                return
            }

            if (item.hasOwnProperty('routeName')) {
                this.router.push({ name: item.routeName })
            }
        },

        openAddTrackingNumbers() {
            const breadCrumbsStore = useBreadCrumbsStore()

            const { addTrackingNumbers } = VIEWS_OPTIONS
            this.currentView = addTrackingNumbers

            breadCrumbsStore.addItem({ title: 'Add Tracking Numbers', view: addTrackingNumbers })
        },

        openBuyNumbers() {
            const breadCrumbsStore = useBreadCrumbsStore()

            const { buyTrackingNumbers } = VIEWS_OPTIONS
            this.currentView = buyTrackingNumbers

            breadCrumbsStore.addItem({ title: 'Buy Tracking Numbers', view: buyTrackingNumbers })
        },

        parsePayload({ numbers, ...other }) {
            this.setNumberPoolId(other.id)
            this.formData = other
            this.attachedNumbers = numbers
        },

        changeSelectedItems(newSelectedItems) {
            this.selected = newSelectedItems
        },

        addAttachedNumbers(numbers) {
            this.attachedNumbers = [...numbers, ...this.attachedNumbers]
        },

        updateTrackingNumberStatus(payload) {
            payload.forEach((item) => {
                const index = this.attachedNumbers.findIndex((i) => i.slug === item.slug)
                if (index !== -1) {
                    this.attachedNumbers.splice(index, 1, item)
                }
            })
        },

        clearSelected() {
            this.selected = []
        },

        _changeAttachedNumberAfterDetach(number_slugs) {
            this.attachedNumbers = this.attachedNumbers.filter(({ slug }) => !number_slugs.includes(slug))
        },

        openDetailVendor() {
            const breadCrumbsStore = useBreadCrumbsStore()

            this.currentView = VIEWS_OPTIONS.detailVendor

            breadCrumbsStore.addItem({ title: 'Detail Vendor', view: VIEWS_OPTIONS.detailVendor.view })
        },

        openAttachVendor() {
            const breadCrumbsStore = useBreadCrumbsStore()

            this.currentView = VIEWS_OPTIONS.attachVendor

            breadCrumbsStore.addItem({ title: 'Setup Vendor', view: VIEWS_OPTIONS.attachVendor.view })
        },

        openAddTrafficSources() {
            const breadCrumbsStore = useBreadCrumbsStore()

            this.currentView = VIEWS_OPTIONS.addTrafficSources

            breadCrumbsStore.addItem({ title: 'Add Traffic Sources', view: VIEWS_OPTIONS.addTrafficSources.view })
        },

        openDetailTrafficSource() {
            const breadCrumbsStore = useBreadCrumbsStore()

            this.currentView = VIEWS_OPTIONS.detailTrafficSource

            breadCrumbsStore.addItem({ title: 'Detail Traffic Source', view: VIEWS_OPTIONS.detailTrafficSource.view })
        },

        openAttachTrafficSource() {
            const breadCrumbsStore = useBreadCrumbsStore()

            this.currentView = VIEWS_OPTIONS.setupNewTrafficSource

            breadCrumbsStore.addItem({ title: 'Setup Traffic Source', view: VIEWS_OPTIONS.setupNewTrafficSource.view })
        },

        async goToLoadDetailNumberPool() {
            if (!this.slug) {
                return
            }

            const defaultStore = useDefaultStore()
            const detailNumberPoolTrafficSourcesStore = useDetailNumberPoolTrafficSourcesStore()

            this.loading = true

            const { success, payload, message } = await getDetailPool(this.slug)

            if (success) {
                this.parsePayload(payload)
                detailNumberPoolTrafficSourcesStore.setData(payload.traffic_sources, payload.id, payload.slug)
            } else {
                defaultStore.setErrorMessage({ message })
            }

            this.loading = false

            return success
        },

        async goToUpdateNumberPool(formDataForUpdate) {
            const defaultStore = useDefaultStore()

            this.updateLoading = true

            const formDataForSend = {
                ...this.formData,
                ...formDataForUpdate,
            }

            if (formDataForSend.vendor && typeof formDataForSend.vendor !== 'string') {
                formDataForSend.vendor = formDataForSend.vendor.slug
            }

            const { success, payload, message } = await updatePool({
                formData: formDataForSend,
                slug: this.slug,
            })

            if (success) {
                this.parsePayload({ ...payload, numbers: this.attachedNumbers })

                defaultStore.setSuccessMessage({ message })
            } else {
                defaultStore.setErrorMessage({ message })
            }

            this.updateLoading = false

            return { success }
        },

        async goToDetachNumbersFromPool({
            numbers = [], number = null, unrent = false, slug,
        }) {
            const defaultStore = useDefaultStore()

            this.actionLoading = true

            const formData = {
                unrent,
            }

            if (number) {
                formData.number_slugs = [number.slug]
            } else {
                formData.number_slugs = numbers.map(({ slug }) => slug)
            }

            const { success, message } = await detachNumbersToPool({ slug, data: formData })

            if (success) {
                this._changeAttachedNumberAfterDetach(formData.number_slugs)
                this.selected = []
                defaultStore.setSuccessMessage({ message })
            } else {
                defaultStore.setErrorMessage({ message })
            }

            this.actionLoading = false

            return success
        },
        async goToLoadAvailableNumbersForPool(withoutLoading = false) {
            const defaultStore = useDefaultStore()

            this.availableLoading = !withoutLoading

            const { success, payload, message } = await getAvailableNumbersForPool(this.slug)

            if (success) {
                this.availableNumbers = payload
            } else {
                defaultStore.setErrorMessage({ message })
            }

            this.availableLoading = false

            return success
        },

        async goToBuyAndAttachNumbersToPool(formData) {
            const defaultStore = useDefaultStore()

            this.actionLoading = true

            const { success, message, payload } = await buyAndAttachNumbersToPool({
                slug: this.slug,
                formData,
            })

            if (success) {
                this.attachedNumbers = [payload, ...this.attachedNumbers]

                this.addPurchasedNumber(payload)

                defaultStore.setSuccessMessage({ message })
            } else {
                defaultStore.setErrorMessage({ message })
            }

            this.actionLoading = false

            return { success }
        },

        async goToAutoBuyAndAttachNumbersToPool(formData) {
            const defaultStore = useDefaultStore()

            this.actionLoading = true

            const { success, message } = await autoBuyPool({
                slug: this.slug,
                formData,
            })

            if (success) {
                defaultStore.setSuccessMessage({ message })
            } else {
                defaultStore.setErrorMessage({ message })
            }

            this.actionLoading = false

            return { success }
        },

        addPurchasedNumber(number) {
            this.purchasedNumbers.push(number)
        },

        changeSearch(value) {
            this.search = value
        },

        changeOnPage(value) {
            this.onPage = value

            setFilterOnPageInLocalStorageByWorkspaceId(value, `${SETTINGS_HEADER_TABLE.numberInPool}.${this.id}`)
        },

        getLocalStorageOnPage() {
            const savedOnPage = getFilterOnPageInLocalStorageByWorkspaceId(`${SETTINGS_HEADER_TABLE.numberInPool}.${this.id}`)

            this.onPage = savedOnPage ? Number(savedOnPage) : 25
        },

        changeAvailableOnPage(value) {
            this.onPageAvailable = value

            setFilterOnPageInLocalStorageByWorkspaceId(value, `${SETTINGS_HEADER_TABLE.availableNumbersInNumberPool}.${this.id}`)
        },

        getLocalStorageAvailableOnPage() {
            const savedOnPage = getFilterOnPageInLocalStorageByWorkspaceId(`${SETTINGS_HEADER_TABLE.availableNumbersInNumberPool}.${this.id}`)

            this.onPageAvailable = savedOnPage ? Number(savedOnPage) : 25
        },
    },
})
